import { useState, useRef } from "react";
import { CURRENCY_MAP } from "../../constants";
import { useCartStore } from "../../store/cartStore";
import { useNavigate } from "react-router-dom";

export default function PublicTablesSection({ event, onSwitchToTickets }) {
  const [tables] = useState(event?.tables || []);
  const [selectedId, setSelectedId] = useState(null);
  const [imgNaturalSize, setImgNaturalSize] = useState({ w: 0, h: 0 });
  const [renderedMapWidth, setRenderedMapWidth] = useState(0);
  const [modal, setModal] = useState(null); // { type, table }

  const mapRef = useRef(null);
  const navigate = useNavigate();

  const { items, tables: cartTables, addTable, clearCart } = useCartStore();
  const selectedTable = tables.find((t) => t.id === selectedId);

  const formatCurrency = (amount) => {
    const symbol = CURRENCY_MAP[event.currency];
    return `${symbol} ${parseFloat(amount).toFixed(2)}`;
  };

  const handleImageLoad = (e) => {
    setImgNaturalSize({
      w: e.target.naturalWidth,
      h: e.target.naturalHeight,
    });
    if (mapRef.current) {
      setRenderedMapWidth(mapRef.current.offsetWidth);
    }
  };

  const goToCheckout = () => {
    navigate("/tickets-store/checkout");
  };

  // ── HANDLE SELECT TABLE → determine which popup to show ──
  const handleSelectTable = (table) => {
    const hasTicketsInCart = items.length > 0;
    const ticketsIncluded = table.tickets_included_enabled === true;

    if (ticketsIncluded) {
      if (hasTicketsInCart) {
        setModal({ type: "remove_tickets", table });
      } else {
        addTable({
          ...table,
          eventId: event.id,
          currency: event.currency,
          client_commission_percentage: event.client_commission_percentage,
          commission_vat_included: event.commission_vat_included,
          commission_vat_percentage: event.commission_vat_percentage,
        });
        goToCheckout();
      }
    } else {
      if (hasTicketsInCart) {
        setModal({ type: "ticket_warning", table });
      } else {
        setModal({ type: "no_tickets", table });
      }
    }
  };

  if (!event?.table_map) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center gap-3 text-gray-500">
        <span className="text-3xl">🗺</span>
        <p className="text-sm">No hay mapa de mesas disponible</p>
      </div>
    );
  }

  return (
    <div className="mt-4 mb-6">
      <p className="text-[10px] tracking-widest text-green-400/70 uppercase">
        MESAS
      </p>
      <h2 className="text-2xl md:text-3xl font-semibold mt-1 mb-6">
        Selecciona tu Mesa
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT — MAP */}
        <div className="bg-[#031c14] border border-green-900/30 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-green-900/20">
            <p className="text-xs text-gray-400">
              Toca una mesa para ver los detalles
            </p>
          </div>

          <div className="relative" ref={mapRef}>
            <img
              src={event.table_map}
              alt="Table map"
              className="w-full object-contain block"
              draggable={false}
              onLoad={handleImageLoad}
            />

            {tables.map((t) => {
              const isSelected = t.id === selectedId;
              const isInCart = cartTables.some((ct) => ct.id === t.id);
              const xPct = imgNaturalSize.w
                ? (t.map_x / imgNaturalSize.w) * 100
                : 0;
              const yPct = imgNaturalSize.h
                ? (t.map_y / imgNaturalSize.h) * 100
                : 0;
              const size = t.map_size || 24;
              const isCircle = t.map_shape === "circle";

              const getHotspotStyle = () => {
                if (isInCart)
                  return "bg-green-500 border-white text-black cursor-pointer";
                if (isSelected)
                  return "bg-green-700 border-white text-white scale-125 cursor-pointer";

                switch (t.display_status) {
                  case "available":
                    return "bg-[#03211a] border-green-900/50 text-gray-400 hover:scale-110 cursor-pointer";
                  case "pending":
                    return "bg-yellow-600/80 border-yellow-400 text-white cursor-not-allowed";
                  case "reserved":
                    return "bg-red-600/80 border-red-400 text-white cursor-not-allowed";
                  case "sold_out":
                    return "bg-red-800/80 border-red-600 text-white cursor-not-allowed";
                  default:
                    return "bg-[#03211a] border-green-900/50 text-gray-400 hover:scale-110 cursor-pointer";
                }
              };

              const isClickable = !["pending", "reserved", "sold_out"].includes(
                t.display_status,
              );

              const hotspotStyle = isInCart
                ? "bg-green-500 border-white text-black"
                : isSelected
                  ? "bg-green-700 border-white text-white scale-125"
                  : "bg-[#03211a] border-green-900/50 text-gray-400 hover:scale-110";

              return (
                <div
                  key={t.id}
                  style={{
                    position: "absolute",
                    left: `${xPct}%`,
                    top: `${yPct}%`,
                    width: `${size}px`,
                    height: `${size}px`,
                    transform: "translate(-50%, -50%)",
                    zIndex: isSelected ? 20 : 10,
                  }}
                >
                  <button
                    onClick={() => {
                      if (!isClickable) return;
                      setSelectedId(t.id === selectedId ? null : t.id);
                    }}
                    style={{
                      width: "100%",
                      height: "100%",
                      fontSize: `${Math.max(7, size * 0.35)}px`,
                      borderRadius: isCircle ? "50%" : "4px",
                      userSelect: "none",
                      cursor: isClickable ? "pointer" : "not-allowed",
                    }}
                    className={`font-bold border-2 shadow-lg flex items-center justify-center w-full h-full transition-all ${getHotspotStyle()}`}
                    title={t.name}
                    disabled={!isClickable}
                  >
                    {t.name}
                  </button>
                </div>
              );
            })}
          </div>

          {/* LEGEND */}
          <div className="px-4 py-3 border-t border-green-900/20 flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded border-2 bg-[#03211a] border-green-900/50" />
              <span className="text-xs text-gray-400">Disponible</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded border-2 bg-green-500 border-white" />
              <span className="text-xs text-gray-400">En carrito</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded border-2 bg-yellow-600/80 border-yellow-400" />
              <span className="text-xs text-gray-400">Pendiente</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded border-2 bg-red-600/80 border-red-400" />
              <span className="text-xs text-gray-400">Reservado / Agotado</span>
            </div>
          </div>
        </div>

        {/* RIGHT — DETAIL PANEL */}
        <div className="bg-[#031c14] border border-green-900/30 rounded-xl flex flex-col min-h-[300px]">
          {selectedTable ? (
            <>
              {/* HEADER */}
              <div className="px-5 py-4 border-b border-green-900/20 flex items-center justify-between">
                <div>
                  <p className="text-[10px] tracking-widest text-green-400/70 uppercase mb-1">
                    Detalles de la mesa
                  </p>
                  <h3 className="text-lg font-semibold text-white">
                    Mesa {selectedTable.name}
                  </h3>
                </div>
                <div className="w-10 h-10 rounded-xl bg-green-900/30 border border-green-700/40 text-green-400 text-sm font-bold flex items-center justify-center flex-shrink-0">
                  {selectedTable.name}
                </div>
              </div>

              {/* DETAILS */}
              <div className="flex-1 p-5 space-y-3 overflow-y-auto">
                {/* TOP 3 CARDS */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-[#021610] border border-green-900/30 rounded-xl p-3 text-center">
                    <p className="text-[10px] tracking-widest text-gray-500 uppercase mb-1">
                      Precio
                    </p>
                    <p className="text-green-400 font-bold text-sm">
                      {selectedTable.display_price != null
                        ? formatCurrency(selectedTable.display_price)
                        : "—"}
                    </p>
                  </div>
                  <div className="bg-[#021610] border border-green-900/30 rounded-xl p-3 text-center">
                    <p className="text-[10px] tracking-widest text-gray-500 uppercase mb-1">
                      Capacidad
                    </p>
                    <p className="text-white font-bold text-sm">
                      {selectedTable.capacity
                        ? `${selectedTable.capacity} pax`
                        : "—"}
                    </p>
                  </div>
                  <div className="bg-[#021610] border border-green-900/30 rounded-xl p-3 text-center">
                    <p className="text-[10px] tracking-widest text-gray-500 uppercase mb-1">
                      Bebidas
                    </p>
                    <p className="text-white font-bold text-sm">
                      {selectedTable.drinks_credit_enabled &&
                      selectedTable.drinks_credit_amount
                        ? formatCurrency(selectedTable.drinks_credit_amount)
                        : "—"}
                    </p>
                  </div>
                </div>

                {/* MERCHANDISE */}
                {selectedTable.merchandise_enabled &&
                  selectedTable.merchandise_description && (
                    <div className="bg-[#021610] border border-green-900/30 rounded-xl p-4">
                      <DetailRow
                        label="🎁 Merchandising incluido"
                        value={
                          <p className="text-gray-300 text-sm leading-relaxed">
                            {selectedTable.merchandise_description}
                          </p>
                        }
                        vertical
                      />
                    </div>
                  )}

                {/* DESCRIPTION */}
                {selectedTable.description && (
                  <div className="bg-[#021610] border border-green-900/30 rounded-xl p-4">
                    <DetailRow
                      label="Descripción"
                      value={
                        <p className="text-gray-300 text-sm leading-relaxed">
                          {selectedTable.description}
                        </p>
                      }
                      vertical
                    />
                  </div>
                )}

                {/* ADD / REMOVE BUTTON */}
                {cartTables.some((ct) => ct.id === selectedTable.id) ? (
                  <button
                    onClick={() =>
                      useCartStore.getState().removeTable(selectedTable.id)
                    }
                    className="w-full py-3 rounded-xl border border-red-500/30 text-red-400 text-sm font-bold tracking-widest uppercase hover:bg-red-500/10 transition"
                  >
                    Eliminar mesa
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      handleSelectTable(selectedTable);
                    }}
                    className="w-full py-3 rounded-xl bg-green-500 text-black font-bold tracking-widest uppercase text-sm hover:bg-green-400 transition"
                  >
                    Reservar mesa →
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 text-gray-500 p-8 text-center">
              <p className="text-sm">Selecciona una mesa en el mapa</p>
              <p className="text-xs text-gray-600">
                Toca cualquier mesa para ver sus detalles
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── MODALS ── */}

      {/* CASE A1 — table includes tickets, cart has tickets */}
      {modal?.type === "remove_tickets" && (
        <TableModal
          title="Ya tienes entradas en tu carrito"
          message={`Tienes ${items.reduce((s, i) => s + i.quantity, 0)} entrada(s) en tu carrito, pero la mesa ${modal.table.name} incluye ${modal.table.capacity || ""} entrada(s). ¿Quieres eliminarlas del carrito?`}
          confirmLabel="Sí, eliminar entradas"
          cancelLabel="No, añadir mesa como extra"
          onConfirm={() => {
            clearCart();
            addTable({
              ...modal.table,
              eventId: event.id,
              currency: event.currency,
              client_commission_percentage: event.client_commission_percentage,
              commission_vat_included: event.commission_vat_included,
              commission_vat_percentage: event.commission_vat_percentage,
            });
            setModal(null);
            goToCheckout();
          }}
          onCancel={() => {
            addTable({
              ...modal.table,
              eventId: event.id,
              currency: event.currency,
              client_commission_percentage: event.client_commission_percentage,
              commission_vat_included: event.commission_vat_included,
              commission_vat_percentage: event.commission_vat_percentage,
            });
            setModal(null);
            goToCheckout();
          }}
          onClose={() => setModal(null)}
        />
      )}

      {/* CASE B1 — no tickets included, cart has tickets */}
      {modal?.type === "ticket_warning" && (
        <TableModal
          title="Asegúrate de tener las entradas correctas"
          message={`Esta mesa no incluye entradas. Asegúrate de que las entradas en tu carrito te dan acceso a la mesa ${modal.table.name}.`}
          confirmLabel="Entendido, continuar"
          onConfirm={() => {
            addTable({
              ...modal.table,
              eventId: event.id,
              currency: event.currency,
              client_commission_percentage: event.client_commission_percentage,
              commission_vat_included: event.commission_vat_included,
              commission_vat_percentage: event.commission_vat_percentage,
            });
            setModal(null);
            goToCheckout();
          }}
          onClose={() => setModal(null)}
        />
      )}

      {/* CASE B2 — no tickets included, no tickets in cart */}
      {modal?.type === "no_tickets" && (
        <TableModal
          title="Esta mesa no incluye entradas"
          message={`La mesa ${modal.table.name} no incluye entradas. Necesitas una entrada para acceder al evento. ¿Quieres comprar entradas ahora?`}
          confirmLabel="Sí, ver entradas"
          cancelLabel="No, solo la mesa"
          onConfirm={() => {
            addTable({
              ...modal.table,
              eventId: event.id,
              currency: event.currency,
              client_commission_percentage: event.client_commission_percentage,
              commission_vat_included: event.commission_vat_included,
              commission_vat_percentage: event.commission_vat_percentage,
            });
            setModal(null);
            if (onSwitchToTickets) onSwitchToTickets();
          }}
          onCancel={() => {
            addTable({
              ...modal.table,
              eventId: event.id,
              currency: event.currency,
              client_commission_percentage: event.client_commission_percentage,
              commission_vat_included: event.commission_vat_included,
              commission_vat_percentage: event.commission_vat_percentage,
            });
            setModal(null);
            goToCheckout();
          }}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

/* ── DETAIL ROW ── */
function DetailRow({ label, value, vertical = false }) {
  return (
    <div
      className={`${vertical ? "space-y-1.5" : "flex items-center justify-between gap-4"}`}
    >
      <p className="text-[11px] tracking-widest text-gray-400 uppercase flex-shrink-0">
        {label}
      </p>
      <div className={vertical ? "" : "text-right"}>{value}</div>
    </div>
  );
}

/* ── TABLE MODAL ── */
function TableModal({
  title,
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  onClose,
}) {
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center px-4 pb-6 sm:pb-0">
      <div className="w-full max-w-sm bg-[#02160e] border border-green-900/40 rounded-2xl p-6 space-y-5 shadow-2xl">
        <div>
          <h2 className="text-lg font-bold text-white mb-2">{title}</h2>
          <p className="text-sm text-gray-400 leading-relaxed">{message}</p>
        </div>
        <div className="flex flex-col gap-3">
          <button
            onClick={onConfirm}
            className="w-full py-3 rounded-xl bg-green-500 text-black font-bold tracking-widests uppercase text-sm hover:bg-green-400 transition"
          >
            {confirmLabel}
          </button>
          {cancelLabel && onCancel && (
            <button
              onClick={onCancel}
              className="w-full py-3 rounded-xl border border-green-900/40 text-gray-400 text-sm tracking-widest uppercase hover:border-green-700/50 hover:text-white transition"
            >
              {cancelLabel}
            </button>
          )}
          <button
            onClick={onClose}
            className="w-full py-2 text-xs text-gray-600 hover:text-gray-400 transition"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
